/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import type { GridConfig, HeaderLevel } from '@coursera-web/cds-labs';

import { Typography2, type Typography2Props, breakpoints, useMediaQuery } from '@coursera/cds-core';

import type {
  CarouselCollection,
  CoreCollectionEntityWithMetadata,
} from 'bundles/collections-carousel/types/CarouselCollection';
import { mapCollectionEntityForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import { getShouldShowCourseraPlusMerchandisingPlacements } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import CollectionModule from 'bundles/page-config-common/components/shared/CollectionModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { COACH_SIDE_COLUMN_GRID_CONFIG } from 'bundles/page-config-common/constants/coachIntegrationConstants';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import { usePageContext } from 'bundles/page-config-common/contexts/PageContext';
import { useCourseraPlusUserData } from 'bundles/page-config-common/contexts/consumer/CourseraPlusUserContext';
import type {
  PageConfig_ComponentConfig_PageConfig_CollectionComponent_Fragment as CollectionComponent,
  PageConfig_ComponentConfigFragment as PageConfigComponent,
} from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import getEventingCollectionIndex from 'bundles/page-config-common/utils/getEventingCollectionIndex';
import CourseraPlusUpsellCard from 'bundles/unified-common/components/coursera-plus-upsells/CourseraPlusUpsellCard';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import { isLIHPProfileBarEnabled } from 'bundles/unified-home-common/utils/isLIHPProfileBarEnabled';

import _t from 'i18n!nls/consumer-home';

const styles = {
  cardWrapper: css`
    grid-auto-rows: 1fr;
    display: grid;
    height: 100%;
  `,
};

// Presentational component that handles the rendering logic
export const CollectionModuleDisplay = ({
  collection,
  eventingCollectionIndex,
  itemsGridConfig,
  currentLevel,
  showCPlusPromo,
  cPlusPromoIndex,
  upsellPlacement,
  isMobile,
  spacing,
  headerTypographyVariant,
  twoColumnLayout,
}: {
  collection: CarouselCollection;
  eventingCollectionIndex: number;
  itemsGridConfig?: GridConfig;
  currentLevel: HeaderLevel;
  showCPlusPromo: boolean;
  cPlusPromoIndex: number;
  upsellPlacement?: 'HIGH' | 'LOW';
  isMobile: boolean;
  spacing?: number;
  headerTypographyVariant: Typography2Props['variant'];
  // This is used to determine if the component should be rendered in a two column layout in the home page
  twoColumnLayout?: boolean;
}) => {
  const isProfileBarEnabled = isLIHPProfileBarEnabled(true);
  return (
    <div data-testid="home-collection-module">
      <HomeSection spacing={spacing} className="PageConfig_CollectionComponent">
        <CollectionModule
          index={eventingCollectionIndex}
          collection={collection}
          itemsGridConfig={itemsGridConfig}
          disableNegativeMargins={isProfileBarEnabled}
          disableCollectionPadding={isProfileBarEnabled}
          customHeader={
            <Typography2 variant={headerTypographyVariant} component={currentLevel}>
              {collection.title}
            </Typography2>
          }
          customComponentAtIndex={
            showCPlusPromo
              ? {
                  index: cPlusPromoIndex,
                  component: (
                    <div css={styles.cardWrapper}>
                      <CourseraPlusUpsellCard
                        title={_t('Unlock access to 10,000+ courses with a subscription.')}
                        mobileBreakpoint={collection.entities.length < 3 ? 'xl' : 'sm'}
                        eventingData={{ index: cPlusPromoIndex, modulePlacement: upsellPlacement }}
                      />
                    </div>
                  ),
                }
              : undefined
          }
          displayRows={showCPlusPromo && isMobile ? 3 : undefined}
          twoColumnLayout={twoColumnLayout}
        />
      </HomeSection>
    </div>
  );
};

const CollectionModuleRenderer = ({
  pageConfigComponent,
  pageComponentList,
  showPromo,
  upsellPlacement,
  spacing = -10,
  headerTypographyVariant = 'titleSmall',
  twoColumnLayout,
}: {
  pageConfigComponent: CollectionComponent;
  pageComponentList: Array<PageConfigComponent | undefined | null>;
  showPromo?: boolean;
  upsellPlacement?: 'HIGH' | 'LOW';
  spacing?: number;
  headerTypographyVariant?: Typography2Props['variant'];
  // This is used to determine if the component should be rendered in a two column layout in the home page
  twoColumnLayout?: boolean;
}) => {
  const pageContext = usePageContext();
  const { coachOpen } = useCoachState();
  const { currentLevel, nextLevel } = useHeaderLevel();
  const { ownsCourseraPlus } = useCourseraPlusUserData();
  const showCPlusPromo = showPromo && !ownsCourseraPlus && getShouldShowCourseraPlusMerchandisingPlacements();
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const isTablet = useMediaQuery(breakpoints.down('sm'));

  let itemsGridConfig = coachOpen ? COACH_SIDE_COLUMN_GRID_CONFIG : undefined;

  if (twoColumnLayout) {
    itemsGridConfig = {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 4,
    };
  }
  const productCardTrackingData =
    pageConfigComponent.collectionEntity.__typename === 'Recommendation_Collection' ||
    pageConfigComponent.collectionEntity.__typename === 'Collection_Collection'
      ? {
          recommender: {
            hash: pageConfigComponent.collectionEntity?.recommenderConfigurationHash,
            context: pageConfigComponent.collectionEntity?.context,
          },
        }
      : {};

  const eventingCollectionIndex = getEventingCollectionIndex(pageConfigComponent.id, pageComponentList);

  const carouselCollection = useMemo(() => {
    if (
      pageConfigComponent.collectionEntity.__typename !== 'Recommendation_Collection' &&
      pageConfigComponent.collectionEntity.__typename !== 'Collection_Collection'
    ) {
      return null;
    }

    const pageConfigCollection = {
      ...pageConfigComponent.collectionEntity,
      metadata: {
        titleComponent: nextLevel ?? 'h3',
      },
    } as CoreCollectionEntityWithMetadata;

    return mapCollectionEntityForCarousel(pageConfigComponent.id, pageConfigCollection, {
      collectionIndex: eventingCollectionIndex,
      carouselRecommender: pageConfigComponent?.recommenderConfigurationHash
        ? {
            hash: pageConfigComponent?.recommenderConfigurationHash,
            // Will replace below with pageConfigComponent.rankingContext once schema change is deployed:
            // https://github.com/webedx-spark/service-definitions/pull/8332
            // context: pageConfigComponent?.context,
          }
        : undefined,
    });
  }, [pageConfigComponent, eventingCollectionIndex, nextLevel]);

  if (!carouselCollection || pageContext?.isEnterprise) {
    return null;
  }

  const collectionProps = {
    ...carouselCollection,
    collectionTrackingData: {
      ...carouselCollection?.collectionTrackingData,
      productCard: productCardTrackingData,
    },
  };

  if (pageConfigComponent.collectionTitleOverride) {
    collectionProps.title = pageConfigComponent.collectionTitleOverride;
  }

  if (pageConfigComponent.secondaryCta) {
    collectionProps.expandedExternalLinkButtonText = pageConfigComponent.secondaryCta.ctaTitle;
    collectionProps.expandedExternalLinkButtonUrl = pageConfigComponent.secondaryCta.ctaUrl;
  }

  let cPlusPromoIndex = 3;
  if (collectionProps.entities.length < 3) {
    cPlusPromoIndex = 1;
  } else if (isMobile) {
    cPlusPromoIndex = 2;
  } else if (isTablet) {
    cPlusPromoIndex = 1;
  }

  return (
    <CollectionModuleDisplay
      collection={collectionProps}
      eventingCollectionIndex={eventingCollectionIndex}
      itemsGridConfig={itemsGridConfig}
      currentLevel={currentLevel}
      showCPlusPromo={showCPlusPromo || false}
      cPlusPromoIndex={cPlusPromoIndex}
      upsellPlacement={upsellPlacement}
      isMobile={isMobile}
      spacing={spacing}
      headerTypographyVariant={headerTypographyVariant}
      twoColumnLayout={twoColumnLayout}
    />
  );
};

export default CollectionModuleRenderer;
